import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  InitialTableState,
  useReactTable,
} from '@tanstack/react-table';

import { useTranslation } from '@mint-lib/i18n';

import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
} from '@myn/mui';

import { RoleType } from '../../api/roles/index.js';
import { UserType } from '../../api/users/index.js';
import NoData from '../../atoms/NoData/index.js';
import useAdditionalHeight from '../../hooks/useAdditionalHeight/useAdditionalHeight.jsx';
import useTablePagination from '../../hooks/useTablePagination/useTablePagination.jsx';
import useTableSorting from '../../hooks/useTableSorting/useTableSorting.jsx';
import PermissionsTableHeaderCell from './PermissionsTableHeaderCell.jsx';
import PermissionsTableRow from './PermissionsTableRow.jsx';

export type GenericTableWrapperType = UserType | RoleType;

type TableWrapperProps<T extends GenericTableWrapperType> = {
  data: T[];
  columns: ColumnDef<T>[];
  tableWrapperSx?: SxProps<Theme>;
  initialState?: InitialTableState;
  dataIsLoading?: boolean;
  noResultsStatus?: boolean;
};

const TableWrapper = <T extends GenericTableWrapperType>({
  data,
  columns,
  tableWrapperSx,
  initialState,
  dataIsLoading,
  noResultsStatus,
}: TableWrapperProps<T>) => {
  const { t } = useTranslation('@myn/permissions');

  // init pagination
  const {
    limit,
    setLimit,
    totalItems,
    currentPage,
    setCurrentPage,
    isPaginationHide,
    rowsPerPageOptions,
  } = useTablePagination();

  // init sorting
  const { ordering, setOrdering, setOrderingBE, field, setField } =
    useTableSorting();

  const table = useReactTable({
    data,
    columns,
    state: {},
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { ...initialState },
  });

  const additionalHeight = useAdditionalHeight({ totalItems, limit });

  return (
    <Box
      sx={{
        height: `calc(100vh - ${additionalHeight()})`,
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2),
        justifyContent: 'space-between',
      }}
    >
      <TableContainer sx={{ ...tableWrapperSx }}>
        <Table
          stickyHeader
          isLoading={dataIsLoading}
          placeholderRowsCount={limit}
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  if (header.column.id === 'actions') {
                    return null;
                  }
                  if (header.column.id === 'expander') {
                    return flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    );
                  }

                  return (
                    <PermissionsTableHeaderCell
                      key={header.id}
                      header={header}
                      field={field}
                      ordering={ordering}
                      dataIsLoading={dataIsLoading}
                      setField={setField}
                      setOrdering={setOrdering}
                      setOrderingBE={setOrderingBE}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {noResultsStatus ? (
              <TableRow>
                <TableCell colSpan={100}>
                  <NoData header={t('No results')} message="" />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {table.getRowModel().rows.map((row) => (
                  <PermissionsTableRow key={row.id} row={row} />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isPaginationHide ? null : (
        <Box sx={{ mr: '100px' }}>
          <TablePagination
            rowsPerPage={limit}
            count={totalItems}
            page={currentPage}
            onPageChange={(_, page) => {
              setCurrentPage(page);
            }}
            type="withSelectAndButtons"
            size="medium"
            rowsPerPageOptions={rowsPerPageOptions}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
            }}
            labelRowsPerPage={t('Items per page:')}
          />
        </Box>
      )}
    </Box>
  );
};

export default TableWrapper;
